import { defineStore } from 'pinia'
import { SetItem, RemoveItem, GetAll, SetValue, GetValue } from '@/io/Storage'
import { Pilot } from '@/class'
import { PilotGroup } from './PilotGroup'
import { CloudController, PortraitController, SaveController } from '@/classes/components'
import * as _ from 'lodash-es'
import { IndexItem } from '@/stores'
import logger from '@/user/logger'
import PilotSheet from './PilotSheet'

export const PilotStore = defineStore('pilot', {
  state: () => ({
    PilotGroups: [] as PilotGroup[],
    Pilots: [] as Pilot[],
    PilotSheets: [] as PilotSheet[],
    CurrentActiveID: '' as string,
  }),
  getters: {
    getPilotGroups: (state: any) => (showDeleted?: boolean) => {
      let out = _.orderBy(state.PilotGroups, 'SortIndex', 'asc')
      if (!showDeleted) out = out.filter((x: PilotGroup) => !x.SaveController.IsDeleted)
      return out
    },
    getPilotByID: (state: any) => (id: string) => {
      return state.Pilots.find((p: Pilot) => p.ID === id)
    },
    getPilotSheetByID: (state: any) => (id: string) => {
      return state.PilotSheets.find((ps: PilotSheet) => ps.Combatant.id === id)
    },
    getGroupByID: (state: any) => (id: string) => {
      return state.PilotGroups.find((p: PilotGroup) => p.ID === id)
    },
    getPilots: (state: any) => (groupID: string, showDeleted?: boolean) => {
      if (!state.Pilots.length) return []
      const group = state.PilotGroups.find((x: PilotGroup) => x.ID === groupID)
      let out = state.Pilots.filter(p => group.Pilots.some(x => x.id === p.ID))
      if (!showDeleted) out = out.filter((x: Pilot) => !x.SaveController.IsDeleted)
      return out
    },
    getUngroupedPilots: (state: any) => {
      const groupedIds = state.PilotGroups.flatMap((x: PilotGroup) => x.Pilots)
      return state.Pilots.filter((p: Pilot) => !groupedIds.includes(p.ID))
    },
    getGroupByPilotID: (state: any) => (pilotID: string) => {
      return state.PilotGroups.find((x: PilotGroup) => x.Pilots.map(x => x.id).includes(pilotID))
    },
    getPilotSheets: (state: any) => {
      if (!state.PilotSheets.length) return []
      return state.PilotSheets
    },
    getMissingDataPilots: (state: any) => {
      return state.Pilots.filter((x: Pilot) => x.BrewController.MissingContent)
    },
    pilotIndexes: (state: any): IndexItem[] => {
      const pilots = state.Pilots.filter((x: Pilot) => !x.SaveController.IsDeleted)
      return pilots.map((x: Pilot) => ({
        id: x.ID,
        title: `${x.Callsign} (${x.Name})`,
        type: 'Pilot',
        pack: '',
        path: `/pilot/${x.ID}`,
        icon: 'cc:pilot',
      }))
    },
    mechIndexes: (state: any): IndexItem[] => {
      const pilots = state.Pilots.filter((x: Pilot) => !x.SaveController.IsDeleted)
      return pilots.flatMap((x: Pilot) =>
        x.Mechs.map(m => ({
          id: m.ID,
          title: m.Name,
          type: `Mech (${x.Callsign} // ${x.Name})`,
          pack: '',
          path: `/pilot/${x.ID}/mech/${m.ID}`,
          icon: 'cc:mech',
        }))
      )
    },
  },
  actions: {
    async LoadPilots(): Promise<void> {
      const pilotGroupData = await GetAll('pilot_groups')
      this.PilotGroups = pilotGroupData.map(x => PilotGroup.Deserialize(x))

      const pilotData = await GetAll('pilots')
      this.Pilots = pilotData.map(x => Pilot.Deserialize(x))

      if (!this.PilotGroups.some(x => x.ID === 'no_group')) {
        this.PilotGroups.push(
          new PilotGroup({
            id: 'no_group',
            name: 'No Group',
            pilots: [],
            description: '',
            history: '',
            sortIndex: -1,
            img: PortraitController.NewPortraitData(),
            save: SaveController.NewSaveData(),
            expanded: true,
          })
        )
      }

      await this.ImportUngroupedPilots()
      await this.LoadPilotSheets()
    },
    async ImportUngroupedPilots(): Promise<void> {
      // import v2 pilots
      // const localData = localStorage.getItem('pilots_v2');
      // if (localData) {
      //   const localPilots = JSON.parse(localData);
      // }

      const groupedIds = this.PilotGroups.flatMap(x => x.Pilots).map(x => x.id)
      const ungroupedPilots = (this.Pilots as Pilot[]).filter(
        (p: Pilot) => !groupedIds.includes(p.ID)
      )

      for (const p of ungroupedPilots) {
        await this.TransferPilot(p as Pilot)
      }
    },
    async AddPilot(pilot: Pilot, groupID?: string): Promise<void> {
      if (this.Pilots.some(x => x.ID === pilot.ID)) {
        logger.info(`Pilot ${pilot.Callsign} already exists`, this)
        // also saves
        this.SetPilot(
          this.Pilots.findIndex(x => x.ID === pilot.ID),
          pilot
        )
        return
      }

      this.Pilots.push(pilot)

      await this.TransferPilot(pilot, groupID)
      await this.SavePilotData()
    },
    async SetPilot(index: number, pilot: Pilot): Promise<void> {
      if (!this.Pilots[index]) return
      this.Pilots.splice(index, 1, pilot)
      await this.SavePilotData()
    },
    async AddGroup(group: PilotGroup): Promise<void> {
      this.PilotGroups.unshift(group)
      await this.SavePilotData()
    },
    async DeleteGroup(group: PilotGroup, deletePilots: boolean): Promise<void> {
      const pilotIDs = group.Pilots.map(p => p.id)

      for (const id of pilotIDs) {
        await this.TransferPilot(this.getPilotByID(id))
      }

      if (deletePilots) {
        for (const id of pilotIDs) {
          this.getPilotByID(id).SaveController.Delete()
        }
      }

      group.SaveController.Delete()
      await this.SavePilotData()
    },
    async DeleteGroupPermanent(group: PilotGroup): Promise<void> {
      this.PilotGroups.splice(this.PilotGroups.indexOf(group), 1)
      await RemoveItem('pilot_groups', group.ID)
    },
    async SavePilotData(pilotIds?: string[]): Promise<void> {
      try {
        const pilotsToSave = pilotIds
          ? this.Pilots.filter(p => pilotIds.includes(p.ID))
          : this.Pilots

        await Promise.all([
          ...pilotsToSave.map(y => SetItem('pilots', Pilot.Serialize(y as Pilot))),
          ...this.PilotGroups.map(x =>
            SetItem('pilot_groups', PilotGroup.Serialize(x as PilotGroup))
          ),
        ])
        logger.info('Pilot data saved')
      } catch (err) {
        logger.error('Error while saving Pilot data', this, err)
      }
    },
    async SaveGroupData(): Promise<void> {
      try {
        await Promise.all(
          this.PilotGroups.map(x => SetItem('pilot_groups', PilotGroup.Serialize(x as PilotGroup)))
        )
        logger.info('Pilot group data saved')
      } catch (err) {
        logger.error('Error while saving Pilot group data', this, err)
      }
    },

    async ClonePilot(payload: Pilot): Promise<void> {
      await this.AddPilot(payload.Clone(), this.getGroupByPilotID(payload.ID))
    },
    async DeletePilotPermanent(pilot: Pilot): Promise<void> {
      const groupIndex = this.PilotGroups.findIndex(x => x.Pilots.map(x => x.id).includes(pilot.ID))

      if (groupIndex > -1) {
        this.PilotGroups[groupIndex].Pilots.splice(
          this.PilotGroups[groupIndex].Pilots.findIndex(p => p.id === pilot.ID),
          1
        )
      }

      this.Pilots.splice(this.Pilots.indexOf(pilot), 1)

      if (pilot.PortraitController.LocalImage) {
        await RemoveItem('images', pilot.PortraitController.LocalImage)
      }

      await RemoveItem('pilots', pilot.ID)

      if (pilot.CloudController.ShareCode) {
        await CloudController.MarkCloudDeleted(pilot.CloudController.Metadata)
      }
    },
    async TransferPilot(p: Pilot, destinationID?: string): Promise<void> {
      const dest = destinationID ?? 'no_group'
      const destinationIndex = this.PilotGroups.findIndex(x => x.ID === dest)

      // Remove from every group except the destination. Iterating all groups (rather
      // than finding a single source) handles corrupted data where a pilot may have
      // been registered in multiple groups by previous code bugs.
      // Use the Pilots setter (not splice) so Vue 3's reactive set trap fires correctly.
      for (const group of this.PilotGroups) {
        if (group.ID === dest) continue
        if (group.Pilots.some(x => x.id === p.ID)) {
          group.Pilots = group.Pilots.filter(x => x.id !== p.ID)
        }
      }

      if (destinationIndex > -1 && !this.PilotGroups[destinationIndex].Pilots.some(x => x.id === p.ID)) {
        this.PilotGroups[destinationIndex].Pilots.push({ id: p.ID, index: -1 })
      }

      await this.SaveGroupData()
    },
    movePilotIndex(group: PilotGroup, from: number, to: number): void {
      this._moveItemInArray(group.Pilots, from, to)
    },
    moveGroupIndex(from: number, to: number): void {
      this._moveItemInArray(this.PilotGroups, from, to)
    },
    _moveItemInArray<T>(array: T[], from: number, to: number): void {
      const item = array.splice(from, 1)[0]
      array.splice(to, 0, item)
      array.forEach((x: any, i) => (x.SortIndex = i))
    },
    ReorderPilot(pilot: Pilot, dir: 'top' | 'up' | 'down' | 'bottom'): void {
      const group = this.getGroupByPilotID(pilot.ID)

      if (dir === 'top') {
        this.movePilotIndex(group, pilot.SortIndex, 0)
      } else if (dir === 'up') {
        this.movePilotIndex(group, pilot.SortIndex, pilot.SortIndex - 1)
      } else if (dir === 'down') {
        this.movePilotIndex(group, pilot.SortIndex, pilot.SortIndex + 1)
      } else if (dir === 'bottom') {
        this.movePilotIndex(group, pilot.SortIndex, group.Pilots.length - 1)
      }

      group.Pilots.forEach((pItem, idx) => {
        const pilot = this.getPilotByID(pItem.id)
        if (pilot) pilot.SortIndex = idx
      })
      this.SavePilotData()
    },
    ReorderGroup(group: PilotGroup, dir: 'top' | 'up' | 'down' | 'bottom'): void {
      const index = this.PilotGroups.findIndex(x => x.ID === group.ID)

      if (dir === 'top') {
        this.moveGroupIndex(index, 0)
      } else if (dir === 'up') {
        this.moveGroupIndex(index, index - 1)
      } else if (dir === 'down') {
        this.moveGroupIndex(index, index + 1)
      } else if (dir === 'bottom') {
        this.moveGroupIndex(index, this.PilotGroups.length - 1)
      }
      this.SavePilotData()
    },
    async LoadPilotSheets(): Promise<void> {
      this.PilotSheets = await GetAll('pilot_sheets').then(data =>
        data.map(x => PilotSheet.Deserialize(x))
      )
      this.LoadSheetId()
    },
    async AddPilotSheet(pilot: Pilot, campaign?: string): Promise<void> {
      const newSheet = PilotSheet.FromPilot(pilot, campaign)
      this.PilotSheets.push(newSheet)
      await SetItem('pilot_sheets', PilotSheet.Serialize(newSheet))
      await this.SetActiveSheet(newSheet.ID)
    },
    async ImportPilotSheet(pilotSheet: PilotSheet): Promise<void> {
      const idx = this.PilotSheets.findIndex(x => x.ID === pilotSheet.ID)
      if (idx !== -1) {
        logger.info(`Pilot sheet ${pilotSheet.Name} already exists`, this)
        this.PilotSheets.splice(idx, 1, pilotSheet)
      } else {
        this.PilotSheets.push(pilotSheet)
      }
      await SetItem('pilot_sheets', PilotSheet.Serialize(pilotSheet))
      await this.SetActiveSheet(pilotSheet.ID)
    },

    async RemovePilotSheet(pilotSheet: PilotSheet): Promise<void> {
      const idx = this.PilotSheets.findIndex(ps => ps.ID === pilotSheet.ID)
      if (idx === -1) return
      this.PilotSheets.splice(idx)
      await RemoveItem('pilot_sheets', pilotSheet.ID)
      if (this.CurrentActiveID === pilotSheet.ID) {
        await this.SetActiveSheet('')
      }
    },
    async SetActiveSheet(id: string): Promise<void> {
      this.CurrentActiveID = id
      await SetValue('current_active_sheet', id)
    },
    async LoadSheetId(): Promise<void> {
      const id = await GetValue('current_active_sheet')
      if (id) this.CurrentActiveID = id
    },
    GetSheet(id: string): PilotSheet | null {
      const sheet = this.PilotSheets.find((ps: any) => ps.ID === id)
      if (sheet) return sheet as PilotSheet
      logger.error('No pilot sheet found with ID ' + id)
      return null
    },
    GetActiveSheet(): PilotSheet | null {
      const activeSheet = this.PilotSheets.find((ps: any) => ps.ID === this.CurrentActiveID)
      if (activeSheet) return activeSheet as PilotSheet
      logger.error('No active pilot sheet found')
      return null
    },
    async AssignActiveSheet(payload: PilotSheet): Promise<void> {
      this.CurrentActiveID = payload.ID
      await SetValue('current_active_sheet', payload.ID)
    },
  },
})
